import React, { useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Accordion, AccordionDetails, AccordionSummary, CardContent, Grid, Paper, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SSOV3 from './Endpoints/SSOV3';
import {
  Challenges,
  ChallengesByID,
  CurrentUser,
  Diets,
  DietsAll,
  DietsCompare,
  DietsDietCode,
  DietsDietCodeQuality,
  SSOV1,
  Users,
  WebhookRetry,
  WorkflowsAll,
  WorkflowsByID,
  WorkflowsByPartnerUserID,
} from './Endpoints';
import ParamTable from './ParamTable';
import { CodeHighlighter } from './index';
import { AdminProgressBar } from '../../../components';
import UserByID from './Endpoints/UserByID';
import UserLogout from './Endpoints/UserLogout';
import UserDelete from './Endpoints/UserDelete';
import UserDeleteToken from './Endpoints/UserDeleteToken';

const useStyles = makeStyles((theme) => ({
  container: {
    margin: 10,
  },
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    '& p': {
      marginTop: 10,
    },
  },
  title: {
    fontSize: '24px !important',
    textAlign: 'left',
    margin: 'auto auto 20px',
  },
  root: {
    width: '100%',
  },
  pre: {
    whiteSpace: 'pre-wrap',
  },
  childAccordion: {
    display: 'block',
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  headingGet: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.info.main,
  },
  headingPost: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.success.main,
  },
  headingPut: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.warning.main,
  },
  headingDelete: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.error.main,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
}));

const APIEndpoints = () => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [subExpanded, setSubExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  const handleSubChange = (panel) => (event, isExpanded) => {
    setSubExpanded(isExpanded ? panel : false);
  };

  const dietsEndpoints = useMemo(() => [
    {
      endpoint: '/api/v3/diets',
      method: 'GET',
      description: 'Endpoints related to various Diet, Quality & Nutrition Information.',
      parameters: [],
    },
    {
      endpoint: '/api/v3/diets/all',
      method: 'GET',
      description: '',
      parameters: [],
    },
    {
      endpoint: '/api/v3/diets/:diet_code',
      method: 'GET',
      description: '',
      parameters: [
        {
          name: 'diet_code',
          description: '',
        },
      ],
    },
    {
      endpoint: '/api/v3/diets/:diet_code/:quality',
      method: 'GET',
      description: '',
      parameters: [
        {
          name: 'diet_code',
          description: '',
        },
        {
          name: 'quality',
          description: '',
        },
      ],
    },
    {
      endpoint: '/api/v3/diets/compare/:diet_code/:quality/:ideal_code/:ideal_quality',
      method: 'GET',
      description: '',
      parameters: [
        {
          name: 'diet_code',
          description: '',
        },
        {
          name: 'quality',
          description: '',
        },
        {
          name: 'ideal_code',
          description: '',
        },
        {
          name: 'ideal_quality',
          description: '',
        },
      ],
    },
  ]);

  return (
    <Grid
      item
      xs={12}
    >
      <a id={'api_endpoints'} />
      <Paper className={classes.patientChartCard}>
        <CardContent>
          <Typography
            variant={'h1'}
            className={classes.title}
          >
            API Endpoints
          </Typography>

          <Accordion
            expanded={expanded === 'sso'}
            onChange={handleChange('sso')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>SSO</Typography>
              <Typography className={classes.secondaryHeading}>
                Endpoints related to creating a patient and logging in.
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Accordion
                className={classes.childAccordion}
                expanded={subExpanded === 'sso1'}
                onChange={handleSubChange('sso1')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>POST</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/sso</Typography>
                </AccordionSummary>
                <>{subExpanded === 'sso1' && <SSOV3 />}</>
              </Accordion>
              <Accordion
                style={{ display: 'none' }}
                expanded={subExpanded === 'sso2'}
                onChange={handleSubChange('sso2')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>POST</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v1/sso - DEPRECATING 2023</Typography>
                </AccordionSummary>
                <>{subExpanded === 'sso2' && <SSOV1 />}</>
              </Accordion>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === 'users'}
            onChange={handleChange('users')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Users</Typography>
              <Typography className={classes.secondaryHeading}>Endpoints related to Diet ID Users.</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Accordion
                className={classes.childAccordion}
                expanded={subExpanded === 'users1'}
                onChange={handleSubChange('users1')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/user</Typography>
                </AccordionSummary>
                <>{subExpanded === 'users1' && <CurrentUser />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'users2'}
                onChange={handleSubChange('users2')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/users/</Typography>
                </AccordionSummary>
                <>{subExpanded === 'users2' && <Users />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'users3'}
                onChange={handleSubChange('users3')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/users/:id</Typography>
                </AccordionSummary>
                <>{subExpanded === 'users3' && <UserByID />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'users4'}
                onChange={handleSubChange('users4')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/user/logout</Typography>
                </AccordionSummary>
                <>{subExpanded === 'users4' && <UserLogout />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'users6'}
                onChange={handleSubChange('users6')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPut}>PUT</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/users/delete/token</Typography>
                </AccordionSummary>
                <>{subExpanded === 'users6' && <UserDeleteToken />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'users5'}
                onChange={handleSubChange('users5')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingDelete}>DELETE</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/users/delete</Typography>
                </AccordionSummary>
                <>{subExpanded === 'users5' && <UserDelete />}</>
              </Accordion>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === 'workflows'}
            onChange={handleChange('workflows')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Workflows</Typography>
              <Typography className={classes.secondaryHeading}>Endpoints related to Assessment Workflows.</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Accordion
                className={classes.childAccordion}
                expanded={subExpanded === 'workflows1'}
                onChange={handleSubChange('workflows1')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/workflows/:id</Typography>
                </AccordionSummary>
                <>{subExpanded === 'workflows1' && <WorkflowsByID />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'workflows2'}
                onChange={handleSubChange('workflows2')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>
                    /api/v3/workflows/partnerUserId/:partnerUserId
                  </Typography>
                </AccordionSummary>
                <>{subExpanded === 'workflows2' && <WorkflowsByPartnerUserID />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'workflows3'}
                onChange={handleSubChange('workflows3')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/workflows/all</Typography>
                </AccordionSummary>
                <>{subExpanded === 'workflows3' && <WorkflowsAll />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'workflows4'}
                onChange={handleSubChange('workflows4')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/workflows/retry_webhook</Typography>
                </AccordionSummary>
                <>{subExpanded === 'workflows4' && <WebhookRetry />}</>
              </Accordion>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'challenges'}
            onChange={handleChange('challenges')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Challenges</Typography>
              <Typography className={classes.secondaryHeading}>Endpoints related to Diet ID Challenges.</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Accordion
                className={classes.childAccordion}
                expanded={subExpanded === 'challenge1'}
                onChange={handleSubChange('challenge1')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/challenges</Typography>
                </AccordionSummary>
                <>{subExpanded === 'challenge1' && <Challenges />}</>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'challenge2'}
                onChange={handleSubChange('challenge2')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingPost}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/challenges/:id</Typography>
                </AccordionSummary>
                <>{subExpanded === 'challenge2' && <ChallengesByID />}</>
              </Accordion>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'healthgoals'}
            onChange={handleChange('healthgoals')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Health Goals</Typography>
              <Typography className={classes.secondaryHeading}>
                Endpoints related to the various Health Goals.
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Accordion
                expanded={subExpanded === 'goals1'}
                onChange={handleSubChange('goals1')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>api/v3/goals</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.childAccordion}>
                  <Typography>
                    <Grid
                      container
                      item
                      xs={12}
                      spacing={2}
                      justifyContent={'space-between'}
                    >
                      {/*
                       <Grid item xs={2}><h3>Example Request</h3></Grid>
                      <Grid item xs={10}>
                      <pre className={classes.pre}>
                      <code>
                    </code>
                      </pre>
                      </Grid>
                      */}
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Example Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter
                          code={`{
  "data": [
    "reduce_cardio_risk",
    "control_bp",
    "prevent_control_diabetes",
    "manage_heart_failure",
    "decrease_inflammation",
    "improve_overall",
    "manage_weight",
    "other",
    "manage_high_cholesterol",
    "manage_food_sensitivities",
    "improve_gut_health"
  ]
}`}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Error Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter
                          code={`{
    "success": "false",
    "message": "Must be logged in"
}
  `}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'goals2'}
                onChange={handleSubChange('goals2')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>api/v3/goals/all</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.childAccordion}>
                  <Typography>
                    <Grid
                      container
                      item
                      xs={12}
                      spacing={2}
                      justifyContent={'space-between'}
                    >
                      {/*
                       <Grid item xs={2}><h3>Example Request</h3></Grid>
                      <Grid item xs={10}>
                      <pre className={classes.pre}>
                      <code>
                    </code>
                      </pre>
                      </Grid>
                      */}
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Example Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter
                          code={`{
	"data": [{
		"name": "Improve heart health",
		"slug": "reduce_cardio_risk",
		"camel_slug": "ReduceCardioRisk",
		"dash-case": "reduce-cardio-risk"
	}, {
		"name": "Control blood pressure",
		"slug": "control_bp",
		"camel_slug": "ControlBloodPressure",
		"dash-case": "control-bp"
	}]
}`}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Error Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter
                          code={`{
    "success": "false",
    "message": "Must be logged in"
}
  `}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'goals3'}
                onChange={handleSubChange('goals3')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>api/v3/goals/:slug</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.childAccordion}>
                  <Typography>
                    <Grid
                      container
                      item
                      xs={12}
                      spacing={2}
                      justifyContent={'space-between'}
                    >
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Example Request</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <ParamTable
                          rows={[
                            {
                              name: 'slug',
                              type: 'string',
                              description: 'The slug of a Diet ID Health Goal found via /goals',
                            },
                          ]}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Example Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter
                          code={`{
  "data": {
    "name": "Decrease Inflammation",
    "slug": "decrease_inflammation",
    "camel_slug": "DecreaseInflammation"
  }
}`}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Error Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter
                          code={`{
    "success": "false",
    "message": "Must be logged in"
}
  `}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === 'diets'}
            onChange={handleChange('diets')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Diets</Typography>
              <Typography className={classes.secondaryHeading}>
                Endpoints related to the various Diet, Quality & Nutrition Information.
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Accordion
                expanded={subExpanded === 'diets1'}
                onChange={handleSubChange('diets1')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/diets</Typography>
                </AccordionSummary>
                <>
                  {subExpanded === 'diets1' && (
                    <React.Suspense fallback={<AdminProgressBar />}>
                      <Diets />
                    </React.Suspense>
                  )}
                </>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'diets2'}
                onChange={handleSubChange('diets2')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/diets/all</Typography>
                </AccordionSummary>
                <>
                  {subExpanded === 'diets3' && (
                    <React.Suspense fallback={<AdminProgressBar />}>
                      <DietsAll />
                    </React.Suspense>
                  )}
                </>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'diets3'}
                onChange={handleSubChange('diets3')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/diets/:diet_code</Typography>
                </AccordionSummary>
                <>
                  {subExpanded === 'diets3' && (
                    <React.Suspense fallback={<AdminProgressBar />}>
                      <DietsDietCode />
                    </React.Suspense>
                  )}
                </>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'diets4'}
                onChange={handleSubChange('diets4')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v3/diets/:diet_code/:quality</Typography>
                </AccordionSummary>
                <>
                  {subExpanded === 'diets4' && (
                    <React.Suspense fallback={<AdminProgressBar />}>
                      <DietsDietCodeQuality />
                    </React.Suspense>
                  )}
                </>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'diets5'}
                onChange={handleSubChange('diets5')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>
                    /api/v3/diets/compare/:diet_code/:quality/:ideal_code/:ideal_quality
                  </Typography>
                </AccordionSummary>
                <>
                  {subExpanded === 'diets5' && (
                    <React.Suspense fallback={<AdminProgressBar />}>
                      <DietsCompare />
                    </React.Suspense>
                  )}
                </>
              </Accordion>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'firehose'}
            onChange={handleChange('firehose')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Firehose</Typography>
              <Typography className={classes.secondaryHeading}>Endpoints related to the Firehose API.</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Accordion
                expanded={subExpanded === 'firehose1'}
                onChange={handleSubChange('firehose1')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>
                    /api/v1/firehose/users/partnerUserId/:partner_user_id
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.childAccordion}>
                  <Typography>
                    <Grid
                      container
                      item
                      xs={12}
                      spacing={2}
                      justifyContent={'space-between'}
                    >
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Example Request</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <ParamTable
                          rows={[
                            {
                              name: 'partner_user_id',
                              type: 'string',
                              description: 'A unique identifier from your database',
                            },
                            {
                              name: 'workflow_date_from',
                              type: 'date',
                              description: 'Filter Results From Workflow Date',
                            },
                            { name: 'workflow_date_to', type: 'date', description: 'Filter Results To Workflow Date' },
                          ]}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Example Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter code={`{}`} />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Error Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter
                          code={`{
    "success": "false",
    "message": "Invalid client secret. Please check your Authorization header (Authorization: 'Bearer xxx')"
}
  `}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={subExpanded === 'firehose2'}
                onChange={handleSubChange('firehose2')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Typography className={classes.headingGet}>GET</Typography>
                  <Typography className={classes.secondaryHeading}>/api/v1/firehose/users/:user_id</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.childAccordion}>
                  <Typography>
                    <Grid
                      container
                      item
                      xs={12}
                      spacing={2}
                      justifyContent={'space-between'}
                    >
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Example Request</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <ParamTable
                          rows={[
                            { name: 'user_id', type: 'string', description: 'The ID of a User.' },
                            {
                              name: 'workflow_date_from',
                              type: 'date',
                              description: 'Filter Results From Workflow Date',
                            },
                            { name: 'workflow_date_to', type: 'date', description: 'Filter Results To Workflow Date' },
                          ]}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Example Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter code={`{}`} />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                      >
                        <h3>Error Response</h3>
                      </Grid>
                      <Grid
                        item
                        xs={10}
                      >
                        <CodeHighlighter
                          code={`{
    "success": "false",
    "message": "Invalid client secret. Please check your Authorization header (Authorization: 'Bearer xxx')"
}
  `}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>

          {/*          <>
            {dietsEndpoints.map((endpoint) => (
              <ApiDocItem
                key={endpoint.endpoint}
                endpoint={endpoint.endpoint}
                method={endpoint.method}
                parameters={endpoint.parameters}
                description={endpoint.description}
              />
            ))}
          </>*/}
        </CardContent>
      </Paper>
    </Grid>
  );
};

const ApiDocItem = ({ endpoint, method, parameters, description }) => (
  <div>
    <h3>{endpoint}</h3>
    <p>{description}</p>
    <h4>Method</h4>
    <p>{method}</p>
    {parameters && (
      <div>
        <h4>Parameters</h4>
        <ul>
          {parameters.map((param) => (
            <li key={param.name}>
              <code>{param.name}</code>: {param.description}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);
export default APIEndpoints;
